import React from "react";
import { AgGridReact, Button, Flex, Grid, InfoBox, Link, Text } from "@jsq/ds/core";
import { NameRenderer } from "../common/NameRenderer";
import { OwnerOrControllerSummaryListActions } from "../common/OwnerOrControllerSummaryListActions";
import { RowItem, TypedAGGridCellRenderParams } from "../common/types";
import { infoBoxStyle, titleStyle } from "./style_vanilla.css";
import { BeneficialOwnerSummaryListProps } from "./types";

export const BeneficialOwnerSummaryList: React.FC<BeneficialOwnerSummaryListProps> = ({
  entityName,
  owners,
  onAddOwner,
  onCompleted,
  onBack,
  onEdit,
  onDelete,
}: BeneficialOwnerSummaryListProps) => {
  const gridOptions: AgGridReact.AgGridReactProps["gridOptions"] = {
    autoSizeStrategy: {
      type: "fitGridWidth",
    },
    suppressCellFocus: true,
    domLayout: "autoHeight",
    rowHeight: 61,
  };
  const colDefs: AgGridReact.AgGridReactProps["columnDefs"] = [
    {
      field: "name",
      headerName: "Name",
      cellRenderer: (params: TypedAGGridCellRenderParams<RowItem>) => (
        <NameRenderer params={params} />
      ),
    },
    {
      field: "relationship",
      headerName: "Relationship",
    },
    {
      field: "ownershipPercentage",
      headerName: "Ownership percentage",
      cellStyle: { textAlign: "right" }, // Align content to the right
      valueFormatter: (params) => `${params.value}%`, // Format the value as `${value}%`
    },
    {
      maxWidth: 100,
      resizable: false,
      suppressMenu: true,
      suppressMovable: true,
      sortable: false,
      field: "actions",
      headerName: "",
      cellRenderer: (params: TypedAGGridCellRenderParams<RowItem>) => (
        <OwnerOrControllerSummaryListActions
          params={params}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ),
    },
  ];

  return (
    <Flex flexDirection="column">
      {entityName && (
        <Link
          to=""
          data-testid="beneficial-owner-selection-back-link"
          variant="muted"
          icon="arrow-left"
          iconPosition="left"
          align="left"
          onClick={onBack}
        >
          {entityName}
        </Link>
      )}
      <Text variant="h3" className={titleStyle}>
        {`Owners of ${entityName}`}
      </Text>
      <InfoBox variant="info" title="Owner" className={infoBoxStyle}>
        <Text variant="regular-md">
          <span>An owner is an individual or entity who owns, directly or indirectly, </span>
          <span style={{ fontWeight: 500 }}>25% or more</span>
          <span>{` of the equity interests of ${entityName}. Select from existing individuals or entities or create a new owner`}</span>
        </Text>
      </InfoBox>

      <Flex flexDirection="column" gap={20}>
        <Flex>
          <Button
            icon="plus"
            onClick={() => {
              onAddOwner();
            }}
          >
            Add owner
          </Button>
        </Flex>
        <Grid gridOptions={gridOptions} rowData={owners} columnDefs={colDefs}></Grid>
        <Flex justifyContent="flex-end" gap={8}>
          <Button
            variant="neutral"
            onClick={() => {
              onBack();
            }}
          >
            Back
          </Button>
          {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
          <Button onClick={() => onCompleted()}>Complete</Button>
        </Flex>
      </Flex>
    </Flex>
  );
};
